import React, { useState, useEffect } from 'react';
import { Plus, TrendingUp, AlertCircle, DollarSign, Calendar, Upload, Settings } from 'lucide-react';
import { useBrand } from '../contexts/BrandContext';
import { forecastService, MonthlyForecast, ForecastScenario } from '../lib/forecastService';
import { useToast } from '../contexts/ToastContext';
import ClientList from './forecast/ClientList';
import ClientForm from './forecast/ClientForm';
import ForecastChart from './forecast/ForecastChart';
import ForecastTable from './forecast/ForecastTable';
import ExpenseList from './forecast/ExpenseList';
import ProfitLossForecast from './forecast/ProfitLossForecast';
import CSVImport from './forecast/CSVImport';
import ScenarioManager from './forecast/ScenarioManager';

const CashFlowForecast: React.FC = () => {
  const { currentBrand, selectedBrandIds } = useBrand();
  const { showToast } = useToast();
  const [forecasts, setForecasts] = useState<MonthlyForecast[]>([]);
  const [monthsToShow, setMonthsToShow] = useState(12);
  const [loading, setLoading] = useState(true);
  const [showClientForm, setShowClientForm] = useState(false);
  const [showCSVImport, setShowCSVImport] = useState(false);
  const [showScenarioManager, setShowScenarioManager] = useState(false);
  const [view, setView] = useState<'overview' | 'clients' | 'expenses' | 'profitloss'>('overview');
  const [selectedScenario, setSelectedScenario] = useState<ForecastScenario | null>(null);
  const [scenarios, setScenarios] = useState<ForecastScenario[]>([]);

  useEffect(() => {
    if (selectedBrandIds.length > 0) {
      loadScenarios();
      loadForecast();
    }
  }, [selectedBrandIds, monthsToShow]);

  useEffect(() => {
    if (selectedBrandIds.length > 0) {
      loadForecast();
    }
  }, [selectedScenario]);

  const loadScenarios = async () => {
    if (selectedBrandIds.length === 0) return;

    try {
      const brandId = selectedBrandIds[0];
      const scenarioList = await forecastService.getScenarios(brandId);
      setScenarios(scenarioList);

      if (!selectedScenario) {
        const defaultScenario = scenarioList.find(s => s.is_default);
        if (defaultScenario) {
          setSelectedScenario(defaultScenario);
        }
      }
    } catch (error) {
      console.error('Error loading scenarios:', error);
    }
  };

  const loadForecast = async () => {
    if (selectedBrandIds.length === 0) return;

    try {
      setLoading(true);
      if (selectedBrandIds.length === 1) {
        const data = await forecastService.generateForecastWithScenario(
          selectedBrandIds[0],
          selectedScenario?.id || null,
          monthsToShow
        );
        setForecasts(data);
      } else {
        const scenarioIds = selectedBrandIds.map(() => selectedScenario?.id || null);
        const data = await forecastService.generateConsolidatedProfitLossWithScenario(
          selectedBrandIds,
          scenarioIds,
          monthsToShow
        );
        setForecasts(data);
      }
    } catch (error) {
      showToast('Failed to load forecast', 'error');
      console.error('Error loading forecast:', error);
    } finally {
      setLoading(false);
    }
  };


  const useChurnAdjusted = selectedScenario && selectedScenario.churn_rate_monthly > 0;
  const totalProjectedRevenue = forecasts.reduce((sum, f) =>
    sum + (useChurnAdjusted ? (f.churn_adjusted_total_revenue || f.total_revenue) : f.total_revenue), 0
  );
  const totalBaselineRevenue = forecasts.reduce((sum, f) => sum + f.total_revenue, 0);
  const totalConfirmedRevenue = forecasts.reduce((sum, f) => sum + f.confirmed_revenue, 0);
  const totalAtRiskRevenue = forecasts.reduce((sum, f) => sum + f.at_risk_revenue, 0);
  const totalChurnLoss = forecasts.reduce((sum, f) => sum + (f.revenue_lost_to_churn || 0), 0);
  const avgMonthlyRevenue = forecasts.length > 0 ? totalProjectedRevenue / forecasts.length : 0;


  if (!currentBrand) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Please select a brand to view cash flow forecast</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Cash Flow Forecast</h1>
          <p className="text-gray-600 mt-1">
            {selectedBrandIds.length > 1
              ? `Consolidated view across ${selectedBrandIds.length} brands`
              : 'Project and track your revenue from recurring services and one-time projects'
            }
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setMonthsToShow(6)}
            className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
              monthsToShow === 6
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            6 Months
          </button>
          <button
            onClick={() => setMonthsToShow(12)}
            className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
              monthsToShow === 12
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            12 Months
          </button>
          <button
            onClick={() => setMonthsToShow(18)}
            className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
              monthsToShow === 18
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            18 Months
          </button>
          <button
            onClick={() => setMonthsToShow(24)}
            className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
              monthsToShow === 24
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            24 Months
          </button>
          <div className="hidden sm:block h-6 w-px bg-gray-300 mx-1" />
          <button
            onClick={() => setShowScenarioManager(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <Settings className="w-5 h-5" />
            Scenarios
          </button>
          <button
            onClick={() => setShowCSVImport(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            <Upload className="w-5 h-5" />
            Import CSV
          </button>
          <button
            onClick={() => setShowClientForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Add Client
          </button>
        </div>
      </div>

      {selectedScenario && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-gray-900">{selectedScenario.scenario_name}</h3>
                {selectedScenario.churn_rate_monthly > 0 && (
                  <span className="px-2 py-1 text-xs font-medium bg-orange-100 text-orange-800 rounded-full">
                    {((1 - Math.pow(1 - selectedScenario.churn_rate_monthly, 12)) * 100).toFixed(1)}% Annual Churn
                  </span>
                )}
              </div>
              {selectedScenario.description && (
                <p className="text-xs text-gray-600 mt-1">{selectedScenario.description}</p>
              )}
              {useChurnAdjusted && totalChurnLoss > 0 && (
                <p className="text-xs text-orange-600 mt-1 font-medium">
                  Projected revenue loss from churn: ${totalChurnLoss.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              )}
            </div>
            <button
              onClick={() => setShowScenarioManager(true)}
              className="px-3 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            >
              Change Scenario
            </button>
          </div>
        </div>
      )}

      <div className="flex gap-2 border-b border-gray-200">
        <button
          onClick={() => setView('overview')}
          className={`px-4 py-2 font-medium transition-colors ${
            view === 'overview'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Revenue Forecast
        </button>
        <button
          onClick={() => setView('profitloss')}
          className={`px-4 py-2 font-medium transition-colors ${
            view === 'profitloss'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Profit & Loss
        </button>
        <button
          onClick={() => setView('expenses')}
          className={`px-4 py-2 font-medium transition-colors ${
            view === 'expenses'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Expenses
        </button>
        <button
          onClick={() => setView('clients')}
          className={`px-4 py-2 font-medium transition-colors ${
            view === 'clients'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Clients
        </button>
      </div>

      {view === 'overview' ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Projected</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    ${totalProjectedRevenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Confirmed Revenue</p>
                  <p className="text-2xl font-bold text-green-600 mt-1">
                    ${totalConfirmedRevenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">At Risk Revenue</p>
                  <p className="text-2xl font-bold text-orange-600 mt-1">
                    ${totalAtRiskRevenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Avg Monthly</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    ${avgMonthlyRevenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
                <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-gray-600" />
                </div>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64 bg-white rounded-lg border border-gray-200">
              <p className="text-gray-500">Loading forecast...</p>
            </div>
          ) : (
            <>
              <ForecastChart forecasts={forecasts} />
              <ForecastTable forecasts={forecasts} />
            </>
          )}
        </>
      ) : view === 'profitloss' ? (
        <ProfitLossForecast
          monthsAhead={monthsToShow}
          selectedBrandIds={selectedBrandIds}
          selectedScenario={selectedScenario}
        />
      ) : view === 'expenses' ? (
        <ExpenseList onRefresh={loadForecast} />
      ) : (
        <ClientList onRefresh={loadForecast} />
      )}

      {showClientForm && (
        <ClientForm
          onClose={() => setShowClientForm(false)}
          onSaved={() => {
            setShowClientForm(false);
            loadForecast();
          }}
        />
      )}

      {showCSVImport && (
        <CSVImport
          onClose={() => setShowCSVImport(false)}
          onSuccess={() => {
            setShowCSVImport(false);
            loadForecast();
          }}
        />
      )}

      {showScenarioManager && (
        <ScenarioManager
          onClose={() => {
            setShowScenarioManager(false);
            loadScenarios();
          }}
          onScenarioSelected={(scenarioId) => {
            const scenario = scenarios.find(s => s.id === scenarioId);
            if (scenario) {
              setSelectedScenario(scenario);
            }
          }}
        />
      )}
    </div>
  );
};

export default CashFlowForecast;